/**
 * ApprovalListPage — 내 결재 이력 (상신/수신 통합 조회).
 *
 * 【역할】
 * - 현재 사용자가 상신했거나 참여한 모든 결재 건 이력 조회
 * - Inbox(내 차례만) 와 달리 전체 상태(PENDING/APPROVED/REJECTED/WITHDRAWN) 표시
 *
 * 【주요 기능】
 * - 목록 조회 (approvalId / title / status / execType / createdAt)
 * - status 컬러 태그: PENDING / APPROVED / REJECTED / WITHDRAWN
 * - 재조회 버튼
 * - 행 클릭 → ApprovalDetailPage 로 이동 (/approvals/:id)
 *
 * 【사용 훅】
 * - useDataSet (ds)
 * - useTransaction
 * - useLabel (t)
 *
 * 【Backend RPC serviceName】
 * - approval/searchList  — 결재 이력 목록
 *
 * 【DataSet 구조】
 * - ds_approval: 결재 목록 (PK: approvalId)
 *
 * 【사용처】 router/index.tsx — `{ path: 'approval/list' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (7-role 승인 체계)
 */
import { useEffect } from 'react'
import { Button, Space, Tag, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useDataSet, useTransaction } from '@/framework'
import type { TrackedRow } from '@/framework/useDataSet'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'

const { Title } = Typography

interface ApprovalRow {
  approvalId: number
  title: string
  status: string
  execType: string
  createdAt?: string
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'processing',
  APPROVED: 'success',
  REJECTED: 'error',
  WITHDRAWN: 'default'
}

/**
 * ApprovalListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ApprovalListPage() {
  const { t } = useLabel()
  const { transaction } = useTransaction()
  const listDs = useDataSet<ApprovalRow>([])

  const load = () =>
    transaction({
      id: 'load',
      service: 'approval/searchList',
      action: 'search',
      inds: { ds_search: { mode: 'mine' } },
      outds: { ds_approval: listDs }
    })

  useEffect(() => {
    load()
  }, [])

  const columns: ColumnsType<TrackedRow<ApprovalRow>> = [
    { title: '#', dataIndex: ['data', 'approvalId'], width: 80 },
    { title: t('LBL_TITLE', '제목'), dataIndex: ['data', 'title'] },
    {
      title: t('LBL_EXEC_TYPE', '진행 방식'),
      dataIndex: ['data', 'execType'],
      width: 110,
      render: (v: string) => <Tag>{v}</Tag>
    },
    {
      title: t('LBL_STATUS', '상태'),
      dataIndex: ['data', 'status'],
      width: 120,
      render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>
    },
    { title: t('LBL_CREATED_AT', '생성일시'), dataIndex: ['data', 'createdAt'], width: 170 }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
        <Title level={3} style={{ margin: 0 }}>
          {t('LBL_APPROVAL_LIST', '내 결재')}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          {t('BTN_REFRESH', '새로고침')}
        </Button>
      </Space>
      <DataTable<TrackedRow<ApprovalRow>>
        rowKey={(r) => String(r.data.approvalId)}
        columns={columns}
        dataSource={listDs.getVisibleRows()}
        size="small"
      />
    </PageContainer>
  )
}
